import React, { memo, useEffect, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { PurpleButton } from 'Components/Button';
import { EquipmentAsset } from 'Containers/SerializedEquipment/types';

interface Props {
  isOpen: boolean;
  asset: EquipmentAsset | null;
  submitting: boolean;
  submitError?: string | null;
  onConfirm: (dateOut?: string) => void;
  onClose: (e: any) => void;
}

// Confirm checking a unit out of its room, with an optional back-dated check-out date (date_out).
// Empty date = server "now". The container owns the optimistic-lock token + idempotency key.
function CheckOutModal({ isOpen, asset, submitting, submitError, onConfirm, onClose }: Props) {
  const [dateOut, setDateOut] = useState('');

  useEffect(() => {
    if (isOpen) setDateOut('');
  }, [isOpen]);

  return (
    <Modal
      id="check-out-asset-modal"
      title="Check Out Equipment"
      isOpen={isOpen}
      modalHeader
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onClose}
    >
      <div className="d-flex flex-column w-100" data-testid="check-out-modal">
        <p className="text-muted">{asset?.name ?? 'Asset'}</p>

        <label className="form-label" htmlFor="check-out-date">
          Check-out date (optional)
        </label>
        <input
          id="check-out-date"
          type="date"
          className="form-control"
          value={dateOut}
          onChange={(e) => setDateOut(e.target.value)}
          data-testid="check-out-date"
        />
        <small className="text-muted">Leave blank to use now.</small>

        {submitError && (
          <p className="text-danger mt-2 mb-0" role="alert" data-testid="check-out-error">
            {submitError}
          </p>
        )}

        <div className="modal-footer d-flex gap-2 justify-content-end mt-3">
          <PurpleButton type="button" onClick={onClose}>
            Cancel
          </PurpleButton>
          <PurpleButton type="button" disabled={submitting} onClick={() => onConfirm(dateOut || undefined)}>
            {submitting ? 'Checking out…' : 'Check out'}
          </PurpleButton>
        </div>
      </div>
    </Modal>
  );
}

CheckOutModal.defaultProps = {
  submitError: null,
};

const CheckOutModalMemo = memo(CheckOutModal, areEqual);

export { CheckOutModalMemo as CheckOutModal };
